import { paymentMemberText } from './payment_identity';
import { useEffect, useId, useRef, useState } from 'react';
import { Alert, Button, Descriptions, Form, Input, Modal, Space, Typography } from 'antd';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { FormField } from '../../shared/form';
import { PaymentExpirySchema } from '../../schemas/subscription';
import type { PaymentExpiryDTO } from '../../schemas/subscription';
import { dateText, display, OrderDetailSchema, triState } from './contracts';
import type { Capabilities, OrderDetail } from './contracts';
import { readPath, subscriptionRequest, SubscriptionRequestError, writeOptions } from './api';
import {
  PaymentExpiryPreviewSchema, PaymentExpiryReceiptSchema,
  canOfferPaymentExpiry, expiryBlockedText, expiryCanCommit, expiryMatchesOrder, expiryReceiptMatchesOrder, expiryReleaseText,
} from './payment_expiry_contracts';
import type { PaymentExpiryIntent, PaymentExpiryPreview, PaymentExpiryReceipt } from './payment_expiry_contracts';

export function PaymentExpiryFacts({ brand, order, preview }: { brand: string; order: OrderDetail; preview: PaymentExpiryPreview | null }) {
  const facts = preview ?? order;
  const reservation = preview ? preview.resetReservation : order.resetReservation;
  return <Descriptions size="small" bordered column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} items={[
    { key: 'brand', label: '目標品牌', children: brand },
    { key: 'member', label: '會員帳號', children: paymentMemberText(facts) },
    { key: 'original', label: '原始商戶單號', children: order.merchantOrderId },
    { key: 'product', label: '商品／版本', children: `${order.productId} / v${order.planVersion}` },
    { key: 'price', label: '應收金額／付款方式', children: `${display(order.expectedAmount)} ${order.currencyCode} / ${order.paymentMethod}` },
    { key: 'payment', label: '支付商付款確認', children: triState(facts.providerPaymentConfirmed) },
    ...(preview ? [{ key: 'providerStatus', label: '支付商狀態', children: display(preview.providerStatus) }] : []),
    { key: 'checked', label: '付款核對時間', children: dateText(facts.checkedAt) },
    { key: 'status', label: '本地訂單狀態', children: facts.paymentStatus },
    { key: 'applied', label: '權益履行狀態', children: facts.entitlementStatus },
    { key: 'deadline', label: '原付款期限', children: dateText(facts.expiresAt) },
    { key: 'slot', label: '待付款名額', children: facts.slotOccupied === null ? '尚無占用資料' : facts.slotOccupied ? '占用 1 個' : '未占用' },
    { key: 'reset', label: '流量重置占用', children: reservation ? `世代 #${reservation.generationId} · ${reservation.state} · 原單 #${reservation.ownerOrderId}` : '尚無重置占用資料' },
  ]} />;
}

export type PaymentExpiryNotice = { type: 'success' | 'info' | 'warning'; text: string };

export function PaymentExpiry({ brand, order, capabilities, onClose, onRefresh, onNotice }: {
  brand: string; order: OrderDetail; capabilities: Capabilities | null; onClose: () => void; onRefresh: () => void; onNotice: (notice: PaymentExpiryNotice) => void;
}) {
  const form = useForm<PaymentExpiryDTO>({ resolver: zodResolver(PaymentExpirySchema), mode: 'onBlur', defaultValues: { reason: '' } });
  const formId = useId();
  const [previewState, setPreview] = useState<{ binding: string; value: PaymentExpiryPreview } | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unresolved, setUnresolved] = useState(false);
  const intent = useRef<{ binding: string; payload: PaymentExpiryIntent } | null>(null);
  const receipt = useRef<PaymentExpiryReceipt | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const controller = useRef<AbortController | null>(null);
  const callbacks = useRef({ onNotice, onRefresh, onClose });
  callbacks.current = { onNotice, onRefresh, onClose };
  const binding = `${brand}:${order.orderId}:${order.merchantOrderId}:${order.memberAccountId}`;
  const effectBinding = useRef(binding);
  const activeBinding = useRef(binding);
  activeBinding.current = binding;
  const current = (request: AbortController, key: string) => alive.current && !request.signal.aborted && controller.current === request && activeBinding.current === key;
  const finish = (request: AbortController, key: string) => {
    // A cancelled StrictMode setup or old original must not unlock its successor.
    if (current(request, key)) { inFlight.current = false; setBusy(false); }
  };
  const authority = order.brand === brand && capabilities?.canReadPayments === true && capabilities.canExpirePayment === true;
  const permitted = authority && canOfferPaymentExpiry(capabilities, order);
  const preview = previewState?.binding === binding ? previewState.value : null;
  const ready = permitted && preview !== null && expiryCanCommit(preview, order, capabilities);
  const knownRejection = (cause: unknown): cause is SubscriptionRequestError => cause instanceof SubscriptionRequestError && cause.status >= 400 && cause.status < 500;
  const rejectionMessage = (cause: SubscriptionRequestError) => cause.errorCode ? expiryBlockedText(cause.errorCode) : cause.message;

  useEffect(() => {
    alive.current = true;
    controller.current?.abort();
    inFlight.current = false;
    if (effectBinding.current !== binding) {
      effectBinding.current = binding;
      setPreview(null); setError(null); setUnresolved(false);
      intent.current = null; receipt.current = null;
      form.reset({ reason: '' });
    }
    const request = new AbortController(); controller.current = request;
    const key = binding;
    const cleanup = () => {
      alive.current = false;
      controller.current?.abort();
    };
    // A same-original authority refresh may cancel transport, never its frozen intent.
    if (intent.current?.binding === binding) { setUnresolved(true); setBusy(false); return cleanup; }
    if (!permitted) { setBusy(false); return cleanup; }
    setPreview(null); setError(null);
    inFlight.current = true; setBusy(true);
    void (async () => {
      try {
        const result = await subscriptionRequest(`/api/admin/payment-orders/${order.orderId}/expire-preview`, PaymentExpiryPreviewSchema, { ...writeOptions(brand, {}), signal: request.signal });
        if (!current(request, key)) return;
        if (!expiryMatchesOrder(result, order)) throw new Error('預覽與所選原單不符，請關閉後重新整理。');
        setPreview({ binding: key, value: result });
      } catch (cause) {
        if (!current(request, key)) return;
        setError(cause instanceof SubscriptionRequestError ? rejectionMessage(cause) : cause instanceof Error ? cause.message : '無法取得原單預覽');
        if (knownRejection(cause) && (cause.status === 403 || cause.status === 409)) {
          callbacks.current.onNotice({ type: 'warning', text: rejectionMessage(cause) });
          callbacks.current.onRefresh(); callbacks.current.onClose();
        }
      } finally { finish(request, key); }
    })();
    return cleanup;
    // Preview facts bind the original, not the operator's editable reason.
    // Callbacks use a ref so parent renders never restart financial requests.
  }, [binding, permitted]);

  const readback = async (request: AbortController, key: string): Promise<boolean> => {
    const after = await subscriptionRequest(readPath(`/api/admin/payment-orders/${order.orderId}`, brand), OrderDetailSchema, { signal: request.signal });
    if (!current(request, key)) return false;
    if (after.brand !== brand || after.orderId !== order.orderId || after.merchantOrderId !== order.merchantOrderId || after.memberAccountId !== order.memberAccountId) throw new Error('讀回原單身分不符，結果尚未確認。');
    if (!after.supportExpiry || !['EXPIRED', 'PAID'].includes(after.paymentStatus)) throw new Error('尚未讀回客服過期紀錄；請保留此原單及原操作請求查證，勿更換請求重新提交。');
    if (receipt.current && after.supportExpiry.commandId !== receipt.current.commandId) throw new Error('原單操作紀錄與回覆不符，請聯絡管理員查證。');
    if (receipt.current) callbacks.current.onNotice({ type: 'success', text: `${after.paymentStatus === 'PAID' ? '已確認人工過期紀錄；原單後續已確認付款成功。' : '原單已設為過期。'}${expiryReleaseText(receipt.current)}` });
    else callbacks.current.onNotice({ type: 'info', text: '已讀回此原單的客服過期紀錄，請核對操作歷程；本次未另計釋放名額。' });
    callbacks.current.onRefresh(); callbacks.current.onClose();
    return true;
  };
  const checkResult = async () => {
    if (inFlight.current || !authority || intent.current?.binding !== binding) return;
    inFlight.current = true; setBusy(true); setError(null);
    const request = new AbortController(); controller.current = request;
    const key = binding;
    try { await readback(request, key); }
    catch (cause) { if (current(request, key)) setError(cause instanceof Error ? cause.message : '讀回結果尚未確認'); }
    finally { finish(request, key); }
  };
  const commit = async (values?: PaymentExpiryDTO) => {
    if (inFlight.current || !authority || (!ready && !unresolved)) return;
    if (!unresolved) {
      if (!values || !preview?.previewVersion) return;
      // Freeze only at the first actual submit. Uncertain retries reuse every byte.
      intent.current = { binding, payload: { reason: values.reason, previewVersion: preview.previewVersion, idempotencyKey: crypto.randomUUID(), confirmMerchantOrderId: order.merchantOrderId } };
    }
    if (intent.current?.binding !== binding) return;
    setUnresolved(true);
    inFlight.current = true; setBusy(true); setError(null);
    const request = new AbortController(); controller.current = request;
    const key = binding;
    try {
      const result = await subscriptionRequest(`/api/admin/payment-orders/${order.orderId}/expire`, PaymentExpiryReceiptSchema, { ...writeOptions(brand, intent.current.payload), signal: request.signal });
      if (!current(request, key)) return;
      if (!expiryReceiptMatchesOrder(result, order)) throw new Error('操作回覆與原單或釋放結果不符，須讀回查證。');
      receipt.current = result;
      await readback(request, key);
    } catch (cause) {
      if (!current(request, key)) return;
      if (knownRejection(cause) && receipt.current === null) {
        setError(rejectionMessage(cause));
        // Explicit rejection never becomes success or an uncertain-key retry.
        setUnresolved(false); setPreview(null); intent.current = null;
        if (cause.status === 403 || cause.status === 409) {
          callbacks.current.onNotice({ type: 'warning', text: rejectionMessage(cause) });
          callbacks.current.onRefresh(); callbacks.current.onClose();
        }
      } else {
        setUnresolved(true);
        setError('操作結果尚未確認。請讀取原單結果，或以完全相同的原請求重試；不要另建付款單。');
      }
    } finally { finish(request, key); }
  };
  const close = () => { if (!inFlight.current) onClose(); };
  return <Modal className="payment-expiry-dialog" title="將未付款原單設為過期" open width={720} onCancel={close} closable={!busy} maskClosable={false} destroyOnClose footer={<Space wrap>
    <Button disabled={busy} onClick={close}>取消</Button>
    {unresolved ? <><Button disabled={busy || !authority} onClick={() => void checkResult()}>重新讀取原單結果</Button><Button danger type="primary" loading={busy} disabled={busy || !authority || intent.current?.binding !== binding} onClick={() => void commit()}>以原請求重試</Button></> : <Button danger type="primary" form={formId} htmlType="submit" loading={busy} disabled={busy || !ready}>確認設為過期</Button>}
  </Space>}>
    <Alert showIcon type="warning" message="設為過期不代表支付商已停止收款" description="ReadyToPay 原單可在後台設為過期並釋放占用；若後續雷馬確認付款成功，原單仍會轉為成功並依原單資格履行。人工過期與付款成功都保留操作歷程。" style={{ marginBottom: 16 }} />
    <PaymentExpiryFacts brand={brand} order={order} preview={preview} />
    {error && <Alert role="alert" type="error" showIcon message={unresolved ? '操作結果尚未確認' : '目前無法完成操作'} description={error} style={{ marginTop: 16 }} />}
    {!permitted && !unresolved && <Alert type="warning" showIcon message="目前沒有此原單的過期操作權限或資格，請重新整理。" />}
    {busy && !preview && <Typography.Paragraph role="status" style={{ marginTop: 16 }}>正在核對原單與支付商付款狀態…</Typography.Paragraph>}
    {preview && <Alert showIcon type={ready ? 'info' : 'warning'} message={ready ? '請核對原單資料、填寫原因後確認' : preview.eligibility === 'ALREADY_COMPLETED' ? '原單已由客服處理' : '原單目前不能安全設為過期'} description={ready ? '實際釋放結果以提交時查證為準；其他購買限制仍適用。' : expiryBlockedText(preview.blockedReason, preview)} style={{ marginTop: 16 }} />}
    <FormProvider {...form}><Form id={formId} layout="vertical" onFinish={form.handleSubmit(values => commit(values))} style={{ marginTop: 16 }}>
      <FormField name="reason" label="過期原因" required control={<Input.TextArea aria-label="過期原因" rows={2} disabled={busy || unresolved} />} />
    </Form></FormProvider>
    {unresolved && <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>原單 #{order.orderId} · {order.merchantOrderId}。查證期間保留原單，不把付款結果不明當成未付款。</Typography.Paragraph>}
  </Modal>;
}
